import { PartComponentType } from "@/pages/configurations/partComponentTypes/models/PartComponentType";
import { usePartsContext } from "@/pages/configurations/parts/context/PartsContext";
import {
	AG_HEADER_HEIGHT,
	AG_ROW_HEIGHT,
	AG_STATUS_BAR_TOTAL_SELECTED,
} from "@/utils/ag-grid-defaults";
import { InputAdornment, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	AgGridEvent,
	ColDef,
	ColumnApi,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	RowSelectedEvent as onSelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Props = {
	partsOfComponentType: {
		[partId: number]: PartComponentType;
	};
	onSelectionChanged: Function;
};

export const DataTable = ({
	partsOfComponentType,
	onSelectionChanged,
}: Props) => {
	const firstTime = useRef(true);

	const [filterInput, setFilterInput] = useState("");

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);

	const {
		state: { parts },
	} = usePartsContext();

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	function sortRows(event: AgGridEvent) {
		event.columnApi.applyColumnState({
			state: [{ colId: "partName", sort: "asc", sortIndex: 0 }],
		});
	}

	const rows = useMemo(() => {
		return parts.map(({ partId, partName }) => {
			return {
				partId,
				partName,
				id: partId,
			};
		});
	}, [parts]);

	const columns: ColDef[] = [
		{
			field: "partId",
			headerName: " # ",
			flex: 1,
			maxWidth: 100,
			checkboxSelection: true,
			showDisabledCheckboxes: true,
			sortIndex: 1,
		},
		{
			field: "partName",
			headerName: "Name",

			flex: 4,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1 * (isInverted ? -1 : 1);
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1 * (isInverted ? -1 : 1);
				} else {
					return (
						nodeA.data.partName?.localeCompare(nodeB.data.partName) *
						(isInverted ? -1 : 1)
					);
				}
			},
		},
	];

	const defaultColDef = useMemo(
		() => ({
			resizable: true,
			sortable: true,
			filter: true,
		}),
		[],
	);

	const onAgGridSelectionChanged = (event: onSelectionChangedEvent) => {
		sortRows(event);
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(event: FirstDataRenderedEvent) => {
			event.api.forEachNode((node) => {
				node.setSelected(
					!!get(partsOfComponentType, get(node.data, "partId", ""), ""),
				);
			});
			sortRows(event);
		},
		[sortRows],
	);

	const onGridReady = useCallback(
		(params: GridReadyEvent) => {
			setGridApi(params.api);
			setGridColumnApi(params.columnApi);
			params.api.sizeColumnsToFit();
		},
		[setGridApi],
	);

	useEffect(() => {
		if (filterInput.trim()) {
			gridApi?.setQuickFilter(filterInput);
		}
	}, [filterInput, gridApi]);

	return (
		<>
			<div style={{ padding: "4px 0 8px" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					margin="dense"
					value={filterInput}
					onChange={handlerFilter}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
			</div>
			<div
				className="ag-theme-balham"
				style={{ width: "100%", height: "60vh" }}
			>
				<AgGridReact
					getRowId={(params) => params.data.partId}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					rowSelection={"multiple"}
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
					rowHeight={AG_ROW_HEIGHT}
					headerHeight={AG_HEADER_HEIGHT}
					statusBar={AG_STATUS_BAR_TOTAL_SELECTED}
				/>
			</div>
		</>
	);
};
